import { useState, useEffect, useRef } from 'react';
import { requestFCMPermission } from '../../utils/fcmUtils';
import { notifPermission } from '../../utils/notifSupport';

const C = {
  bg:   'var(--smc-deep, #060b18)',
  panel:'var(--smc-panel, #0d1526)',
  bd:   'var(--smc-bd, rgba(255,255,255,0.08))',
  tx:   'var(--smc-tx, #e8edf5)',
  tx2:  'var(--smc-tx2, #8892a4)',
  tx3:  'var(--smc-tx3, #4d5a6e)',
  ac:   'var(--smc-ac, #0e9cc0)',
  acb:  'var(--smc-ac-bg, rgba(14,156,192,0.10))',
  acbr: 'var(--smc-ac-border, rgba(14,156,192,0.28))',
  mono: "'Share Tech Mono',monospace",
  sans: "'Inter',sans-serif",
};

function isStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://')
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

// ── Install step ─────────────────────────────────────────────────────────────
function InstallStep({ installEvt, onInstall, onSkip }) {
  const ios = isIOS();

  return (
    <div style={{ textAlign: 'center', padding: '8px 4px' }}>
      <div style={{ fontSize: 44, marginBottom: 12 }}>🚀</div>
      <div style={{ fontFamily: C.mono, fontSize: 12, color: C.tx, marginBottom: 6, letterSpacing: 1 }}>
        INSTALL SPACEMANCRAFT
      </div>
      <div style={{ fontFamily: C.sans, fontSize: 12, color: C.tx2, lineHeight: 1.6, marginBottom: 20 }}>
        {ios
          ? 'Add to your Home Screen for the full app experience — offline access, faster loading, and push notifications.'
          : 'Install the app for a native experience — works offline, launches instantly, and supports push notifications.'}
      </div>
      {ios ? (
        <div style={{ background: C.acb, border: `0.5px solid ${C.acbr}`,
          borderRadius: 10, padding: '12px 16px', marginBottom: 20, textAlign: 'left' }}>
          <div style={{ fontFamily: C.mono, fontSize: 9, color: C.ac, marginBottom: 8, letterSpacing: 1 }}>
            HOW TO INSTALL ON iOS
          </div>
          {['Tap the Share button ⬆ in Safari', 'Scroll down and tap "Add to Home Screen"', 'Tap "Add" in the top-right corner'].map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', background: C.ac,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: C.mono, fontSize: 8, color: '#060b18', flexShrink: 0, marginTop: 1 }}>
                {i + 1}
              </div>
              <div style={{ fontFamily: C.sans, fontSize: 11, color: C.tx2 }}>{s}</div>
            </div>
          ))}
        </div>
      ) : (
        <button onClick={onInstall}
          style={{ width: '100%', padding: '13px', background: C.ac, border: 'none',
            borderRadius: 10, fontFamily: C.mono, fontSize: 11, color: '#060b18',
            fontWeight: 700, cursor: 'pointer', letterSpacing: 1, marginBottom: 10 }}>
          INSTALL NOW
        </button>
      )}
      <button onClick={onSkip}
        style={{ width: '100%', padding: '11px', background: 'transparent',
          border: `0.5px solid ${C.bd}`, borderRadius: 10, fontFamily: C.mono,
          fontSize: 10, color: C.tx3, cursor: 'pointer', letterSpacing: 1 }}>
        {ios ? 'I\'ll do it later' : 'Not now'}
      </button>
    </div>
  );
}

// ── Notification step ────────────────────────────────────────────────────────
function NotifStep({ userId, onDone, onSkip }) {
  const [loading,  setLoading]  = useState(false);
  const [denied,   setDenied]   = useState(notifPermission() === 'denied');

  async function allow() {
    setLoading(true);
    const token = await requestFCMPermission(userId);
    setLoading(false);
    if (token || notifPermission() === 'granted') {
      onDone();
    } else if (notifPermission() === 'denied') {
      setDenied(true);
    }
  }

  return (
    <div style={{ textAlign: 'center', padding: '8px 4px' }}>
      <div style={{ fontSize: 44, marginBottom: 12 }}>🔔</div>
      <div style={{ fontFamily: C.mono, fontSize: 12, color: C.tx, marginBottom: 6, letterSpacing: 1 }}>
        ENABLE NOTIFICATIONS
      </div>
      <div style={{ fontFamily: C.sans, fontSize: 12, color: C.tx2, lineHeight: 1.6, marginBottom: 20 }}>
        Stay on top of experiment updates, salary slips, assigned tasks, and team messages — in real time.
      </div>

      {denied ? (
        <div style={{ background: '#E24B4A15', border: '0.5px solid #E24B4A40',
          borderRadius: 8, padding: '12px 14px', marginBottom: 16, textAlign: 'left' }}>
          <div style={{ fontFamily: C.mono, fontSize: 9, color: '#E24B4A', marginBottom: 6, letterSpacing: 1 }}>
            NOTIFICATIONS BLOCKED
          </div>
          <div style={{ fontFamily: C.sans, fontSize: 11, color: C.tx2, lineHeight: 1.5 }}>
            Your browser has blocked notifications. To enable them: open your browser settings, find this site under <b>Notifications</b>, and set it to <b>Allow</b>.
          </div>
        </div>
      ) : (
        <button onClick={allow} disabled={loading}
          style={{ width: '100%', padding: '13px', background: C.ac, border: 'none',
            borderRadius: 10, fontFamily: C.mono, fontSize: 11, color: '#060b18',
            fontWeight: 700, cursor: loading ? 'default' : 'pointer',
            letterSpacing: 1, marginBottom: 10, opacity: loading ? 0.7 : 1 }}>
          {loading ? 'REQUESTING…' : 'ALLOW NOTIFICATIONS'}
        </button>
      )}

      <button onClick={onSkip}
        style={{ width: '100%', padding: '11px', background: 'transparent',
          border: `0.5px solid ${C.bd}`, borderRadius: 10, fontFamily: C.mono,
          fontSize: 10, color: C.tx3, cursor: 'pointer', letterSpacing: 1 }}>
        {denied ? 'Close' : 'Skip for now'}
      </button>
    </div>
  );
}

// ── Main modal ───────────────────────────────────────────────────────────────
export default function StartupChecks({ userId, role }) {
  const [step,        setStep]       = useState(null); // null | 'install' | 'notif'
  const [installEvt,  setInstallEvt] = useState(null);
  const installEvtRef = useRef(null);

  // Capture beforeinstallprompt immediately (fires very early)
  useEffect(() => {
    function handler(e) {
      e.preventDefault();
      installEvtRef.current = e;
      setInstallEvt(e);
    }
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Decide which step to show once user is logged in
  useEffect(() => {
    if (!role || !userId) return;

    // Small delay so the main app renders first
    const t = setTimeout(() => {
      const standalone      = isStandaloneMode();
      const notifGranted    = !('Notification' in window) || notifPermission() === 'granted';
      const hasInstallEvt   = !!installEvtRef.current;
      const ios             = isIOS();

      if (!standalone && (hasInstallEvt || ios)) {
        setStep('install');
      } else if (!notifGranted) {
        setStep('notif');
      }
      // If already installed AND notif granted → nothing to show
    }, 1200);

    return () => clearTimeout(t);
  }, [role, userId]);

  // Auto-register FCM token silently when permission is already granted
  useEffect(() => {
    if (!userId || !role) return;
    if (!('Notification' in window)) return;
    if (notifPermission() === 'granted') {
      requestFCMPermission(userId).catch(() => {});
    }
  }, [userId, role]);

  async function handleInstall() {
    const evt = installEvtRef.current;
    if (!evt) return;
    evt.prompt();
    const { outcome } = await evt.userChoice;
    installEvtRef.current = null;
    setInstallEvt(null);
    if (outcome === 'accepted') {
      // Move to notif step after successful install
      advanceFromInstall();
    } else {
      advanceFromInstall();
    }
  }

  function advanceFromInstall() {
    const notifGranted = !('Notification' in window) || notifPermission() === 'granted';
    if (!notifGranted) {
      setStep('notif');
    } else {
      setStep(null);
    }
  }

  if (!step) return null;

  return (
    <div style={{
      position:   'fixed', inset: 0, zIndex: 9990,
      background: 'rgba(6,11,24,0.88)',
      display:    'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(6px)',
      padding: '20px',
    }}>
      <div style={{
        background:   C.panel,
        border:       `0.5px solid ${C.bd}`,
        borderRadius: 16,
        padding:      '28px 24px',
        maxWidth:     380,
        width:        '100%',
        boxShadow:    '0 24px 64px rgba(0,0,0,0.7)',
        position:     'relative',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <img src="/logo-dark.png" alt="Spacemancraft"
            style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }} />
        </div>

        {/* Step indicator */}
        {(step === 'notif') && !isStandaloneMode() && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginBottom: 16 }}>
            {['install', 'notif'].map(s => (
              <div key={s} style={{
                width: 6, height: 6, borderRadius: '50%',
                background: s === step ? C.ac : C.bd,
                transition: 'background 0.3s',
              }} />
            ))}
          </div>
        )}

        {step === 'install' && (
          <InstallStep
            installEvt={installEvt}
            onInstall={handleInstall}
            onSkip={advanceFromInstall}
          />
        )}

        {step === 'notif' && (
          <NotifStep
            userId={userId}
            onDone={() => setStep(null)}
            onSkip={() => setStep(null)}
          />
        )}
      </div>
    </div>
  );
}
